import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSearchParams } from 'hooks'
import { customerioTracker } from 'lib'
import { useTranslate } from 'lib/tolgee'
import { useBrandTerritoryResolver } from 'modules/brands/common/hooks'
import { BRAND_CONTEXTS_BY_TYPE } from 'modules/brands/common/hooks/use-brand-niche-context-manager/use-brand-niche-context-manager.consts'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import type { INicheTab } from './niche-tab.types'
import { ViewState } from './niche-tab.types'
import { useNicheContextManager, useNicheWithContext } from '../common/hooks'
import { SYSTEM_CONTEXT_IDS } from '../common/niche.consts'

export const useNicheTabModel = ({
  territoryId,
  brandType,
  feature,
  trackingTab = 'niches',
  hideCreateContext,
  skipContextsQuery,
  providers,
  uploadDateParam,
}: INicheTab) => {
  const [isCreateDashboardModalOpen, setIsCreateDashboardModalOpen] = useState(false)
  const searchParams = useSearchParams()
  const initialContextId = searchParams.get('contextId') ?? undefined

  const { getTerritoryIdForContext: brandTerritoryResolver, isReady: isBrandTerritoryReady } =
    useBrandTerritoryResolver({
      territoryId: territoryId,
      brandContentType: brandType,
    })
  const brandNicheContexts = brandType ? BRAND_CONTEXTS_BY_TYPE[brandType] : undefined

  const { isGeneratingHash, error, createNicheError, handleRetrySelectContext, ...contextManager } =
    useNicheContextManager({
      territoryId: territoryId,
      initialContextId,
      customDefaultContexts: brandNicheContexts,
      territoryIdResolver: brandTerritoryResolver,
      readinessCheck: brandType ? { isReady: isBrandTerritoryReady } : undefined,
      hideCreateContext: hideCreateContext,
      skipContextsQuery: skipContextsQuery,
    })
  const { niches, nicheStatus, handleRefetch, hasError, unsubscribe, allNichesHaveErrors, generatedNicheDescription } =
    useNicheWithContext({
      providers: providers,
      territoryId,
      uploadDateParam: uploadDateParam,
      contextHash: contextManager.activeContext?.hash,
    })

  const t = useTranslate()

  const isEmpty = useMemo(() => {
    return niches.every(item => {
      const hasViews = item.avgViews
      const hasEngagement = item.avgEngagement
      const hasPosts = item.totalPosts
      const hasData = hasViews || hasEngagement || hasPosts
      return !hasData
    })
  }, [niches])

  const loadingRowsCount = useMemo(() => {
    const baseCount = 20 - niches.length
    return baseCount > 0 ? baseCount : 0
  }, [niches.length])

  const nicheForDashModal = useMemo(() => {
    return niches.map(item => {
      return {
        id: item.id,
        name: item.name,
      }
    })
  }, [niches])

  const isHappeningNow = useMemo(() => {
    return contextManager.activeContext.id === SYSTEM_CONTEXT_IDS.happeningNow
  }, [contextManager.activeContext.id])

  const shouldDisplayContextDescription = useMemo(() => {
    const isGeneratedAndEmpty = nicheStatus === 'completed' && !generatedNicheDescription
    return !contextManager.isDefaultContext && !isGeneratedAndEmpty && !isHappeningNow
  }, [contextManager.isDefaultContext, generatedNicheDescription, isHappeningNow, nicheStatus])

  const handleDashboardModal = () => {
    setIsCreateDashboardModalOpen(prevState => !prevState)
  }

  const handleOpenDashboardModal = () => {
    const { TOP_OPEN_CREATE_REPORT } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(TOP_OPEN_CREATE_REPORT, {
      entityId: territoryId,
    })
    handleDashboardModal()
  }

  const handleTrackClick = useCallback(
    ({ name }: { name: string }) => {
      if (!feature) return

      customerioTracker.track(customerioTracker.eventNames.CLICK_NICHE, {
        feature,
        action: 'click_page',
        tab: trackingTab,
        name,
      })
    },
    [feature, trackingTab]
  )

  const tableTitle = useMemo(() => {
    return t({ ...TOLGEE_GLOBALS.results, parameters: { results: niches.length } })
  }, [niches.length, t])

  const viewState = useMemo(() => {
    if (createNicheError) return ViewState.ERROR

    if (!nicheStatus || isGeneratingHash) return ViewState.GENERATING

    if (nicheStatus === 'empty') return ViewState.EMPTY

    if ((nicheStatus === 'error' && !niches.length) || hasError || allNichesHaveErrors) return ViewState.ERROR

    if (nicheStatus === 'pending' || (nicheStatus === 'completed' && isEmpty)) {
      return ViewState.FETCHING
    }

    if (nicheStatus === 'completed' || (nicheStatus === 'error' && niches.length)) {
      unsubscribe()
      return ViewState.COMPLETE
    }

    return ViewState.GENERATING
  }, [
    createNicheError,
    nicheStatus,
    isGeneratingHash,
    niches.length,
    hasError,
    allNichesHaveErrors,
    isEmpty,
    unsubscribe,
  ])

  const iconAnimationRef = useRef(null)

  const onRetry = useCallback(() => {
    if (createNicheError || error) {
      handleRetrySelectContext()
    }
    if (hasError) {
      handleRefetch()
    }
  }, [error, createNicheError, handleRefetch, handleRetrySelectContext, hasError])

  const onComponentMount = useCallback(() => {
    if (iconAnimationRef.current && viewState === ViewState.FETCHING) {
      iconAnimationRef.current.startAnimation()
    }
  }, [viewState])

  const onComponentUnmount = useCallback(() => {
    return () => {
      if (iconAnimationRef.current && viewState !== ViewState.FETCHING) {
        iconAnimationRef.current.stopAnimation()
      }
    }
  }, [viewState])

  useEffect(onComponentMount, [onComponentMount])

  useEffect(onComponentUnmount, [onComponentUnmount])

  return {
    niches,
    nicheStatus,
    viewState,
    tableTitle,
    loadingRowsCount,
    onRetry,
    isCreateDashboardModalOpen,
    handleDashboardModal,
    nicheForDashModal,
    generatedNicheDescription,
    shouldDisplayContextDescription,
    isHappeningNow,
    handleOpenDashboardModal,
    handleTrackClick,
    entityId: territoryId,
    ...contextManager,
  }
}
